// Original educational scenarios; checked 2026-11-05.
export const householdLessons = [
  {
    "id": "household-shared-cash",
    "moduleId": "household",
    "title": "兩個人的帳本，怎樣合成家庭收支？",
    "subtitle": "共同範圍、內部轉帳與分攤",
    "minutes": 6,
    "tag": "一起整理家庭財務",
    "objectives": [
      "定義家庭合併範圍",
      "避免把轉入共同帳戶又算一次收入或支出"
    ],
    "paragraphs": [
      "先約定要一起看哪些人、帳戶與用途，再合併對外收入與支出。家庭範圍內從個人帳戶移到共同帳戶，只改變位置；若同時當支出和家庭新收入，會放大收支。",
      "公平分攤不只有一種公式。可以討論固定金額、按收入比例或按不同責任安排；照顧工作、收入波動和各自保留的自主支出也要一起談。分攤規則不直接決定財產法律歸屬。",
      "使用虛構數字演練即可，不必向工具提供伴侶帳戶、身分資訊或完整財產清單。以下只是已達成共識的一種算式，不代表所有家庭都應採用。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設甲每月實領50,000元、乙30,000元，家庭對外生活支出40,000元。",
        "雙方約定按實領收入比例分攤，甲占62.5%、乙37.5%，各負擔25,000元與15,000元。",
        "先將這些錢轉入共同帳戶，再支付40,000元生活費；家庭對外支出仍是40,000元，不是80,000元。"
      ],
      "conclusion": "先約定共同範圍與規則，再看每一筆錢是否真的離開家庭。"
    },
    "pitfalls": [
      "把內部轉帳重複當成家庭支出。",
      "把某種分攤公式當成唯一公平或法律規定。"
    ],
    "glossary": [
      {
        "term": "家庭合併範圍",
        "definition": "這份預算共同納入的人、帳戶與用途範圍。"
      },
      {
        "term": "內部轉帳",
        "definition": "在已定義的合併範圍內移動資金，不會因此創造對外收入。"
      }
    ],
    "takeaway": "先約定共同範圍與規則，再看每一筆錢是否真的離開家庭。",
    "exercise": "以虛構家庭畫出個人帳戶、共同帳戶與外部商家的箭頭，圈出真正的對外收支。",
    "quiz": {
      "question": "家庭共同支出36,000元，約定收入比2：1分攤，各是多少？",
      "options": [
        "各18,000元",
        "24,000元與12,000元",
        "36,000元與18,000元"
      ],
      "answer": 1,
      "explanation": "36,000×2/3＝24,000，另一方12,000；這是題設約定，不是唯一分攤方式。"
    },
    "sources": [
      {
        "label": "美國 FTC｜編列收支預算的通用方法",
        "url": "https://consumer.gov/your-money/making-budget",
        "kind": "official"
      },
      {
        "label": "美國 CFPB｜帳單日曆的通用方法",
        "url": "https://www.consumerfinance.gov/archive/blog/budget-help-manage-your-monthly-expenses-bill-calendar/",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台幣家庭案例；本課為通用預算與溝通練習，不推定夫妻財產、扶養或繼承的法律關係。"
  },
  {
    "id": "household-sinking-funds",
    "moduleId": "household",
    "title": "每月都有剩錢，為什麼繳年費時還是不夠？",
    "subtitle": "年度支出、到期日與專款準備",
    "minutes": 6,
    "tag": "一起整理家庭財務",
    "objectives": [
      "把可預期的年度支出放進每月安排",
      "依距離付款的時間計算準備速度"
    ],
    "paragraphs": [
      "保費、學費、稅費或維修等支出可能不是每月出現，卻可以事先安排。年平均預算回答長期每月攤多少；距離下次付款還有幾個月，則決定這次需要多快補足。",
      "先列付款日期、目標金額和已備資金，再把缺口除以剩餘可存月份。把錢移到專用帳戶是準備動作，不應在實際付款時又把同一成本重複算成兩次支出。",
      "可預期支出與無法預知的緊急事件分開整理，更容易知道每筆錢是否已有用途。預算是安排，不代表收入或支出必定照預期發生。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設每年固定支出60,000元，長期按12個月平均是每月5,000元。",
        "但下次付款只剩10個月，現在尚未備款，這一輪每月需準備6,000元。",
        "若誤用5,000元，十個月後僅50,000元，還差10,000元；下一年度再依完整週期重排。"
      ],
      "conclusion": "平均每月花多少，與下次到期前要存多少，是不同問題。"
    },
    "pitfalls": [
      "忽略已經過去的月份，所有年費都直接除12。",
      "把專款準備與實際付款算成兩次對外花費。"
    ],
    "glossary": [
      {
        "term": "年度專款",
        "definition": "事先為已知用途和到期日準備的資金。"
      },
      {
        "term": "付款日曆",
        "definition": "按日期列出收入與應付項目，用來檢查時點是否銜接。"
      }
    ],
    "takeaway": "平均每月花多少，與下次到期前要存多少，是不同問題。",
    "exercise": "為三筆虛構年度支出列到期日、已有金額及剩餘準備月數。",
    "quiz": {
      "question": "六個月後要付48,000元，目前專款已有12,000元，不計利息，每月還需準備多少？",
      "options": [
        "4,000元",
        "6,000元",
        "8,000元"
      ],
      "answer": 1,
      "explanation": "缺口48,000−12,000＝36,000元，除6為每月6,000元。"
    },
    "sources": [
      {
        "label": "美國 CFPB｜帳單日曆的通用方法",
        "url": "https://www.consumerfinance.gov/archive/blog/budget-help-manage-your-monthly-expenses-bill-calendar/",
        "kind": "official"
      },
      {
        "label": "美國 FTC｜編列收支預算的通用方法",
        "url": "https://consumer.gov/your-money/making-budget",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台幣家庭案例；本課為通用預算與溝通練習，不推定夫妻財產、扶養或繼承的法律關係。"
  },
  {
    "id": "household-debt-cost",
    "moduleId": "household",
    "title": "先還大筆債，還是先看利率與條款？",
    "subtitle": "利息、最低付款與提前清償成本",
    "minutes": 6,
    "tag": "一起整理家庭財務",
    "objectives": [
      "分開比較餘額、成本率與付款時程",
      "理解額外還款對未來利息的簡化影響"
    ],
    "paragraphs": [
      "大筆餘額不一定有較高的成本率，也不一定是唯一需要先處理的項目。先確保各筆約定付款與生活現金，再核對有效成本、違約後果、是否有擔保及提前清償限制。",
      "在其他條件相同、無額外費用的簡化比較中，同樣本金減少放在較高利率債務，通常能少付較多之後利息。但違約金、優惠期限及緊急現金需求會影響實際選擇。",
      "下面只比較下一個月利息，年率除12、餘額固定且不計複利，不是完整貸款攤還或轉貸建議。不要把未來預期投資報酬當成確定可抵掉的借款成本。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設A餘額100,000元、年利率18%；B餘額300,000元、年利率3%。下一個月利息分別1,500元與750元。",
        "若額外還本金10,000元且無違約金，A下一個月少150元利息，B少25元。",
        "這個比較固定其他條件，只回答利息差；仍要保留生活支出、履行各筆約定並檢查實際契約。"
      ],
      "conclusion": "先看完整成本與付款責任，再比較同一筆額外資金的效果。"
    },
    "pitfalls": [
      "只因餘額較大就判定應優先全部清償。",
      "忽略其他債務的約定付款，讓其中一筆逾期。"
    ],
    "glossary": [
      {
        "term": "成本率",
        "definition": "在明確期間與費用口徑下衡量的借款成本比例。"
      },
      {
        "term": "額外還本金",
        "definition": "在約定付款外降低貸款本金的動作，效果與費用依契約而定。"
      }
    ],
    "takeaway": "先看完整成本與付款責任，再比較同一筆額外資金的效果。",
    "exercise": "用虛構資料列出餘額、成本率、每期應付、到期日與提前清償條件，不輸入真實帳號。",
    "quiz": {
      "question": "無其他費用，額外還本金20,000元到年利率12%的債務，按年率除12，下一個月利息約少多少？",
      "options": [
        "2,400元",
        "200元",
        "20,000元"
      ],
      "answer": 1,
      "explanation": "20,000×12%÷12＝200元，只是下一個月的簡化利息差。"
    },
    "sources": [
      {
        "label": "行政院消保會｜總費用年百分率的涵義",
        "url": "https://cpc.ey.gov.tw/Page/4432D6D5FA6677B9/51d0fc32-0842-4849-855c-f72a5eef31b2",
        "kind": "official"
      },
      {
        "label": "銀行局｜個人購屋貸款定型化契約應記載事項",
        "url": "https://law.banking.gov.tw/Chi/FLAW/PrintFLAW02.aspx?lsid=FL075099",
        "kind": "official"
      },
      {
        "label": "美國 CFPB｜緊急預備金的通用方法",
        "url": "https://www.consumerfinance.gov/an-essential-guide-to-building-an-emergency-fund/",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台幣家庭案例；本課為通用預算與溝通練習，不推定夫妻財產、扶養或繼承的法律關係。"
  },
  {
    "id": "household-resilience",
    "moduleId": "household",
    "title": "家裡少一份收入，預算可以撐多久？",
    "subtitle": "壓力情境與家庭檢查日",
    "minutes": 6,
    "tag": "一起整理家庭財務",
    "objectives": [
      "以現金缺口估算假設可支應時間",
      "安排共同檢視及緊急資料取得方式"
    ],
    "paragraphs": [
      "應變練習先問收入少多少、必要支出能否調整、哪些款項仍必須按時付。以「可動用預備金÷每月缺口」估算，與直接除以全部支出回答的問題不同；要把分母寫清楚。",
      "預備金可能已有指定用途，不能把變現不確定或有法律、契約限制的資產全部算成可立即使用。模型還可能漏掉突發醫療、修繕與恢復收入的時間，所以估算不是保證能撐到某一天。",
      "可約定定期檢查收支、保障與債務的日期，並確認緊急時誰知道文件存放及可聯絡的機構。只需記錄流程與存放位置，不把密碼寫進共享課程筆記，也不推定家庭成員有法定代辦權。"
    ],
    "example": {
      "title": "假設案例，逐步核對",
      "steps": [
        "假設原家庭實領80,000元，必要現金支出55,000元；事件後收入降至35,000元，支出暫不變。",
        "每月缺口為20,000元，另有確定可用且未重複指定用途的預備金120,000元。",
        "在收入支出均固定、無額外事件或收益的題設下，可補足6個月缺口；它不是保證，也不同於完全沒有收入時能支付多久。"
      ],
      "conclusion": "算清楚分母與假設，再把資料和決策流程安排好。"
    },
    "pitfalls": [
      "把預備金除全部支出與除收入缺口的月數混用。",
      "把恢復收入時間當作確定，卻沒有備用安排。"
    ],
    "glossary": [
      {
        "term": "每月現金缺口",
        "definition": "同一期間必要現金支出超過可用收入的金額。"
      },
      {
        "term": "壓力情境",
        "definition": "刻意改變收入、支出或利率等條件，檢查安排是否承受得住的假設練習。"
      }
    ],
    "takeaway": "算清楚分母與假設，再把資料和決策流程安排好。",
    "exercise": "以紙上假想數字做一次收入減少測試，另寫下每月家庭檢查日與待確認事項。",
    "quiz": {
      "question": "必要支出45,000元、事件後收入30,000元，可用預備金90,000元，假設不變，可補足幾個月缺口？",
      "options": [
        "2個月",
        "3個月",
        "6個月"
      ],
      "answer": 2,
      "explanation": "每月缺口15,000元，90,000÷15,000＝6個月；用全部支出作分母會回答另一個問題。"
    },
    "sources": [
      {
        "label": "美國 CFPB｜緊急預備金的通用方法",
        "url": "https://www.consumerfinance.gov/an-essential-guide-to-building-an-emergency-fund/",
        "kind": "official"
      },
      {
        "label": "美國 CFPB｜帳單日曆的通用方法",
        "url": "https://www.consumerfinance.gov/archive/blog/budget-help-manage-your-monthly-expenses-bill-calendar/",
        "kind": "official"
      }
    ],
    "verifiedAt": "2026-11-05",
    "scopeNote": "台幣家庭案例；本課為通用預算與溝通練習，不推定夫妻財產、扶養或繼承的法律關係。"
  }
];
